/**
 * Loop-guard predicate for the OTel exporters' own telemetry traffic.
 *
 * The browser trace instrumentations (fetch / XHR) create a span for every
 * outbound request. The OTLP exporters ALSO send over HTTP — so without a guard,
 * each export POST becomes a span, which is exported, which becomes a span … a
 * self-amplifying loop. This is the "span storm" that got frontend tracing
 * disabled in May 2026.
 *
 * We break it by matching the request PATH, not the full URL. The pre-disable
 * guard was an exact scheme+host+PORT regex built from the configured ingest URL,
 * which included the default `:443`. URL normalisation strips `:443`, so the
 * exporter's own POSTs stopped matching and the loop returned. Matching the path
 * is immune to port / host / scheme normalisation, and to the split where traces
 * egress to the collector while logs+metrics still egress to SigNoz — ANY
 * `/v1/{traces,metrics,logs}` request is ignored, whatever the host.
 *
 * Scope note: this matches `/v1/{traces,metrics,logs}` anywhere in the URL. That
 * is safe because the app's own API lives under `/api/v1/<resource>` where the
 * resource is never `traces`/`metrics`/`logs` (bills, integration, projects, …),
 * so no real request is masked.
 *
 * `OTEL_INGEST_PATH` is passed to FetchInstrumentation / XMLHttpRequestInstrumentation
 * `ignoreUrls`; a matching URL yields no span.
 */
export const OTEL_INGEST_PATH = /\/v1\/(traces|metrics|logs)(\?|$)/;

/** True when `url` is an OTLP ingest endpoint that must NOT be traced (loop guard). */
export const isOtelIngestUrl = (url: string): boolean =>
  OTEL_INGEST_PATH.test(url);
